import React, { useEffect, useRef, useState } from "react";
import { compressImageToBase64 } from "../../Utils";

interface PatientPhotoUploadProps {
  base64Image: string | null;
  previewUrl: string | null;
  onChange: (base64: string | null, previewUrl: string | null) => void;
  onError?: (message: string) => void;
}

export default function PatientPhotoUpload({
  base64Image,
  previewUrl,
  onChange,
  onError,
}: PatientPhotoUploadProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const resetInputs = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      onError?.("Please select an image file");
      resetInputs();
      return;
    }

    try {
      const { base64, dataUrl } = await compressImageToBase64(file);
      onChange(base64, dataUrl);
      setMenuOpen(false);
    } catch {
      onError?.("Failed to process image. Please try another photo.");
    } finally {
      resetInputs();
    }
  };

  const handleRemove = () => {
    onChange(null, null);
    setMenuOpen(false);
    resetInputs();
  };

  return (
    <div className="patient_photo_upload" ref={rootRef}>
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <button
        type="button"
        className="patient_photo_upload__trigger"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label="Patient photo (optional)"
        aria-expanded={menuOpen}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Patient"
            className="patient_photo_upload__preview"
          />
        ) : (
          <span className="patient_photo_upload__icon" aria-hidden>
            📸
          </span>
        )}
      </button>

      <span className="patient_photo_upload__hint">
        Photo
        <br />
        (optional)
      </span>

      {menuOpen && (
        <div className="patient_photo_upload__menu" role="menu">
          <button
            type="button"
            className="patient_photo_upload__menu-item"
            role="menuitem"
            onClick={() => cameraInputRef.current?.click()}
          >
            <span aria-hidden>📸</span> Take Photo
          </button>
          <button
            type="button"
            className="patient_photo_upload__menu-item"
            role="menuitem"
            onClick={() => uploadInputRef.current?.click()}
          >
            <span aria-hidden>📁</span> Upload
          </button>
          {base64Image && (
            <>
              <hr className="patient_photo_upload__menu-divider" />
              <button
                type="button"
                className="patient_photo_upload__menu-item patient_photo_upload__menu-item--remove"
                role="menuitem"
                onClick={handleRemove}
              >
                <span aria-hidden>🗑️</span> Remove
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
